import { Box, Center, Flex, FormControl, FormLabel, HStack, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, StyleProps, Text } from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react" ;
import { SubmitHandler, useForm } from "react-hook-form";

import { BaseSubscriptionDto, OnlineSubscriptionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { corporationIdentifiersDictionary, G1Country } from "@bcasmart/doxecure-commons/doxecureLocales";
import { CorporationIdentifiersDictionary, CorporationIdentifierType } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $keys, $ok } from "foundation-ts/commons";
import { TSCountry } from "foundation-ts/tscountry";
import { TSDate } from "foundation-ts/tsdate";
import { $country } from 'foundation-ts/tsdefaults';
import { Address, Countries, country, Nullable, uint, UUID } from "foundation-ts/types";

// import { AddressForm } from "@components/AddressForm/AddressForm";
import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { CorporationForm } from "@components/CorporationForm/CorporationForm";
import { G1Input } from "@components/G1Input";
import { G1Button } from "@components/G1Button";
import { SeaShellBox } from "@components/SeaShellBox";
import { config } from "@/config/config";
import { useDevice } from "@hooks/useDevice";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@utils/icons";
import { ActionButton } from "@utils/TypesAndConstants";

export interface CorporationParameters {
    apid?: UUID,
    createdAt?: Nullable<TSDate>,
    valid?: boolean,
    name?: string;
    corporationIdentifierType?: CorporationIdentifierType;
    corporationIdentifier?: string;
    address?: Address;
    vatNumber?: string;
    billingAddress?: Address;
    currentMaxUsers?: uint;
    minUsers?: uint;
    nbActiveUsers?: Nullable<number>;
    mandate?: {
        type: "sepa" | "creditcard";
        cardExpiryDate?: Nullable<TSDate>;
    }
};

type CorporationInfosProperties = {
    data: CorporationParameters;
    actionButtons: ActionButton<CorporationParameters>[];
    subscriptionInfos?: Nullable<BaseSubscriptionDto>;
    countries?: G1Country[];
    availableCorporationIdentifiers?: Map<country, CorporationIdentifiersDictionary>;
    disableOrganizationName?: boolean;
    disableOtherFields?: boolean;
    disableLimitOfUser?: boolean;
    displayInformation?: Nullable<JSX.Element>;
    onBack?: () => void;
} & StyleProps;

export const CorporationInfos = (props: CorporationInfosProperties) => {
    const { data, actionButtons, subscriptionInfos, visibility, maxWidth, disableOtherFields, disableOrganizationName, disableLimitOfUser, displayInformation, countries, availableCorporationIdentifiers, onBack } = props ;
    const [sameAddress, setSameAddress] = useState(true) ;
    // const [oldBillingAddress, setOldBillingAddress] = useState<Nullable<Address>>(null) ;
    // const [oldApiId, setOldApiId] = useState<Nullable<UUID>>(null) ;
    const [corporationIdentifiersDict, setCorporationIdentifiersDict] = useState<CorporationIdentifiersDictionary>({}) ;
    const [currentMaxUsers, setCurrentMaxUsers] = useState(0);
    const [showNbActiveUsers, setShowNbActiveUsers] = useState(false);
    const { isMOBILE } = useDevice() ;
    const {
        getValues,
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors },
    } = useForm<any>({
        mode: "onChange",
        reValidateMode: "onChange",
    }) ;

    const activeSubscription: Nullable<OnlineSubscriptionDto> = !!subscriptionInfos?.offline ? null : subscriptionInfos as OnlineSubscriptionDto;

    // const handleChangeCheckboxBilling = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setSameAddress(e.target.checked) ;
    //     // When viewing a corporation with a billingAddress different of its address and user check 'same address', we save the old billingAddress so we can show no specific billingAddress
    //     // And this allows that if user unchecks 'same address', we can show again the old billingAddress
    //     if (e.target.checked) {
    //         if ($ok(data.billingAddress)) {
    //             setOldBillingAddress(data.billingAddress) ;
    //             setOldApiId(data.apid) ;
    //             data.billingAddress = undefined ;
    //         }
    //     } else {
    //         if ((data.apid === oldApiId) && $ok(oldBillingAddress)) { // Display old billingAddress corresponding to the right corporate
    //             data.billingAddress = oldBillingAddress! ;
    //         }
    //     }
    // }

    const handleChangeCollaboratorsLimit = (_valueAsString: string, valueAsNumber: number) => {
        setCurrentMaxUsers(($ok(data.minUsers) && valueAsNumber < data.minUsers!) ? data.minUsers! : valueAsNumber);
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValue(name, value) ;
    }

    const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const newCountry = TSCountry.country(e.currentTarget.value)?.alpha2Code ;
        if ($ok(newCountry)) {
            const newDict = availableCorporationIdentifiers?.get(newCountry!) ;
            if ($ok(newDict)) { 
                setCorporationIdentifiersDict(newDict!) ;
                setValue("organizationIdentifierType", $keys(newDict).first()) ;
            }
        }
        setValue("organizationCountry", e.currentTarget.value) ;
    }

    const onSubmit: SubmitHandler<any> = (values: any, event) => {
        const corporationParams: CorporationParameters = {
            apid: values.apid || undefined,
            name: values.organizationName,
            corporationIdentifierType: values.organizationIdentifierType,
            corporationIdentifier: values.organizationIdentifier,
            currentMaxUsers: currentMaxUsers as uint,
            minUsers: disableLimitOfUser ? data.minUsers : values.minUsers,
            nbActiveUsers: disableLimitOfUser ? data.nbActiveUsers : values.nbActiveUsers,
            address: {
                streetNumber: $ok(values.streetNumber) && (values.streetNumber.length > 0) ? values.streetNumber : undefined,
                street: $ok(values.street) && (values.street.length > 0) ? values.street : undefined,
                complement: $ok(values.streetComplement) && (values.streetComplement.length > 0) ? values.streetComplement : undefined,
                zipCode: $ok(values.zipCode) && (values.zipCode.length > 0) ? values.zipCode : undefined,
                city: values.city.toUpperCase(),
                country: $ok(values.organizationCountry) ? $country(values.organizationCountry)! : Countries.FR
            }
        } ;
        if (values.organizationIdentifierType === "VAT" || ($ok(values.vatNumber) && (values.vatNumber.length > 0))) {
            corporationParams.vatNumber = values.organizationIdentifierType === "VAT" ? values.organizationIdentifier : data.vatNumber ;
        }
        if (sameAddress) {
            corporationParams.billingAddress = undefined ;
        } else if ($ok(values["billing-city"]) && (values["billing-city"].length > 0)) {
            corporationParams.billingAddress = {
                streetNumber: $ok(values["billing-streetNumber"]) && (values["billing-streetNumber"].length > 0) ? values["billing-streetNumber"] : undefined,
                street: $ok(values["billing-street"]) && (values["billing-street"].length > 0) ? values["billing-street"] : undefined,
                complement: $ok(values["billing-streetComplement"]) && (values["billing-streetComplement"].length > 0) ? values["billing-streetComplement"] : undefined,
                zipCode: $ok(values["billing-zipCode"]) && (values["billing-zipCode"].length > 0) ? values["billing-zipCode"] : undefined,
                city: values["billing-city"].toUpperCase(),
                country: $ok(values["billing-country"]) ? $country(values["billing-country"])! : Countries.FR
            } ;
        }

        const button = (event?.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const buttonIndex = button.getAttribute('data-index');
        if ($ok(buttonIndex)) {
            actionButtons[buttonIndex!].onSubmit(corporationParams) ;
        }
    } ;

    const _validateCorporationIdentifier = (id: string): boolean => {
        const selectedCountry = getValues("organizationCountry") ;
        const selectedCorporationIdentifierType = getValues("organizationIdentifierType") ;
        return corporationIdentifiersDictionary[selectedCorporationIdentifierType!]!.validate(id, selectedCountry) ;
    }

    useEffect(() => {
        reset() ;
        const newCountry = TSCountry.country(data.address?.country || Countries.FR)?.alpha2Code ;
        if ($ok(newCountry)) {
            const newDict = availableCorporationIdentifiers?.get(newCountry!) ;
            if ($ok(newDict)) { 
                setCorporationIdentifiersDict(newDict!) ;
            }
        }

        setSameAddress(!$ok(data.billingAddress));
        setValue("organizationName", data.name || "");
        setValue("organizationCountry", data.address?.country);
        setValue("organizationIdentifier", data.corporationIdentifier || "");
        setValue("organizationIdentifierType", data.corporationIdentifierType);
        setValue("apid", data.apid);
        setValue("nbActiveUsers", data.nbActiveUsers);
        setShowNbActiveUsers($ok(data?.nbActiveUsers));
        setValue("streetNumber", data.address?.streetNumber || "");
        setValue("street", data.address?.street || "");
        setValue("streetComplement", data.address?.complement || "");
        setValue("zipCode", data.address?.zipCode || "");
        setValue("city", data.address?.city || "");
        if ($ok(data.billingAddress)) {
            setValue("billing-streetNumber", data.billingAddress?.streetNumber || "") ;
            setValue("billing-street", data.billingAddress?.street || "") ;
            setValue("billing-streetComplement", data.billingAddress?.complement || "") ;
            setValue("billing-zipCode", data.billingAddress?.zipCode || "") ;
            setValue("billing-city", data.billingAddress?.city || "") ;
            setValue("billing-country", data.billingAddress?.country || data.address?.country || Countries.FR) ;
        }

        setCurrentMaxUsers(data.currentMaxUsers!);
        if (disableLimitOfUser) {
            setValue("nbMaxUsers", data.currentMaxUsers!);
        }
    }, [data, availableCorporationIdentifiers]) ;

    return (
        <>
            {onBack &&
                <ColumnTitle display={isMOBILE ? "block" : "none"} color={$g1Color('global.write')}>
                    <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                        <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                        <Text>Retour</Text>
                    </Flex>
                </ColumnTitle>
            }

            {displayInformation}

            { $ok(subscriptionInfos) && (
                <SeaShellBox component="form">
                    <Text fontWeight="bold">{!!subscriptionInfos?.offline ? 'Compte client créé par un administrateur' : 'Compte client ouvert en ligne'}</Text>
                    <Text>Date {!!subscriptionInfos?.offline ? "de création" : "d'inscription"}: {data.createdAt?.toString("%d %B %Y")}</Text>
                    {!!subscriptionInfos?.offline ? "" : (
                        <>
                            <Text>Date de renouvellement: {activeSubscription?.endSubscriptionDate.toString("%d %B %Y")}</Text>
                            <Text>Période d'essai: {$ok(activeSubscription?.trialPeriod) ? `En cours (se termine le ${activeSubscription?.trialPeriod?.end.toString("%d %B %Y")})` : "Terminée"}</Text>
                            <Text>Abonnement illimité: {!!activeSubscription?.unlimited ? "Oui" : "Non"}</Text>
                            <Text>Type de mandat: {data.mandate?.type === "sepa" ? "Mandat SEPA" : `Mandat Carte Bancaire (expire fin ${data.mandate?.cardExpiryDate?.toString("%m/%y")})`}</Text>
                        </>
                    )}
                    {!!subscriptionInfos?.offline ?
                        <HStack>
                            <FormLabel fontSize="1.25em" mb={0} mr={0}>{showNbActiveUsers ? "Nombre de collaborateurs définis :" : "Nombre de collaborateurs autorisés"}</FormLabel>
                            <G1Input component="form" registerInput={{...register("nbActiveUsers")}} hidden={!showNbActiveUsers} disabled width="75px" textAlign="center" />
                            <Text hidden={!showNbActiveUsers}> / </Text>
                            {disableLimitOfUser ? 
                                <G1Input component="form" registerInput={{...register("nbMaxUsers")}} hidden={!currentMaxUsers} disabled width="75px" textAlign="center" />
                                :
                                <NumberInput value={currentMaxUsers} min={data.minUsers!} step={config.admin.stepCorpUsers} minWidth="90px" width="90px" onChange={handleChangeCollaboratorsLimit}
                                             bg={$g1Color(`form.field.bg`)} color={$g1Color(`form.field.write`)} borderColor={$g1Color(`form.field.border`)}
                                             _hover={{ borderColor: $g1Color(`form.field.hover.border`) }} _focus={{ borderColor: $g1Color(`form.field.select.border`) }}>
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            }
                        </HStack>
                        : <Text>Nombre de collaborateurs: {data.minUsers} / {$ok(activeSubscription?.trialPeriod) ? 5 : subscriptionInfos?.maxUsers}</Text>
                    }
                    {$ok(activeSubscription?.requestCancelSubscription) && 
                        <Text color="red" mt={3}>Demande de résiliation: {activeSubscription?.requestCancelSubscription?.requestDate.toString("%d %B %Y")} effectuée par {activeSubscription?.requestCancelSubscription?.requesterName}</Text>
                    }
                    {$ok(activeSubscription?.requestReduceUsers) && (
                        <Text color="red" mt={3}>Demande de réduction de {activeSubscription?.requestReduceUsers?.reduceCount} collaborateurs effectuée le {activeSubscription?.requestReduceUsers?.requestedDate?.toString("%d %B %Y")} par {activeSubscription?.requestReduceUsers?.requesterName}</Text>
                    )}
                    {$ok(activeSubscription?.downgradeRenewalToStandard) && (
                        <Text color="red" mt={3}>Demande de retour à l'abonnement standard effectuée le {activeSubscription?.downgradeRenewalToStandard?.requestedDate?.toString("%d %B %Y")} par {activeSubscription?.downgradeRenewalToStandard?.requesterName}</Text>
                    )}
                </SeaShellBox>
            )}

            <Box visibility={visibility} width="100%" mt={5} maxW={maxWidth}>
                <SeaShellBox component="form">
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <CorporationForm label="Nom de l'organisation" required={true} disabledOrganizationName={disableOrganizationName} disabledVATNumber={disableOtherFields} disabledAddress={disableOtherFields} showUniqueId={true}
                            countries={countries}
                            corporationIdentifiersDict={corporationIdentifiersDict}
                            selectedCIT={data.corporationIdentifierType}
                            errors={errors}
                            register={register}
                            onChange={handleChange}
                            onCountryChange={handleCountryChange}
                            validate={_validateCorporationIdentifier} />

                        {/* <Box borderWidth="1px" rounded={{ base: "0", md: "md" }} borderColor="grey" padding={2} marginTop="20px !important">
                            <Box display="flex" alignItems="center" position="relative" top="-22px" width={"fit-content"} paddingX="1" bg={$g1Color('form.local.bg')}>
                                <Checkbox
                                    id="cbBilling"
                                    name="cbBilling"
                                    isChecked={sameAddress}
                                    disabled={disableFields}
                                    bg={$g1Color('form.field.bg')}
                                    color={$g1Color('form.field.write')}
                                    borderColor={$g1Color('form.field.border')}
                                    _hover={{ borderColor: $g1Color('form.field.hover.border') }}
                                    _focus={{ borderColor: $g1Color('form.field.select.border') }}
                                    onChange={handleChangeCheckboxBilling} />
                                <span style={{ marginLeft: '5px' }}>Adresse de facturation identique à l'adresse de l'organisation ?</span>
                            </Box>
                            {!sameAddress && <AddressForm prepId="billing-" required={!sameAddress} disabled={sameAddress} firstPosition={true} countries={countries} errors={errors} register={register} onChange={handleChange} />}
                        </Box> */}

                        {/* <Box height="1px" width="75%" borderWidth="1px" borderColor="lightgrey" margin='0 auto' mt={4} /> */}
                        {/* <FormControl marginTop={3}>
                            <HStack>
                                <FormLabel fontSize="1.25em" mb={0} mr={0}>{showNbActiveUsers ? "Nombre de collaborateurs définis :" : "Nombre de collaborateurs autorisés"}</FormLabel>
                                <G1Input component="form" registerInput={{...register("nbActiveUsers")}} hidden={!showNbActiveUsers} disabled width="75px" textAlign="center" />
                                <Text hidden={!showNbActiveUsers}> / </Text>
                                {disableLimitOfUser ? 
                                    <G1Input component="form" registerInput={{...register("nbMaxUsers")}} hidden={!currentMaxUsers} disabled width="75px" textAlign="center" />
                                    :
                                    <NumberInput value={currentMaxUsers} min={data.minUsers!} step={config.admin.stepCorpUsers} minWidth="90px" width="90px" onChange={handleChangeCollaboratorsLimit}
                                                 bg={$g1Color(`form.field.bg`)} color={$g1Color(`form.field.write`)} borderColor={$g1Color(`form.field.border`)}
                                                 _hover={{ borderColor: $g1Color(`form.field.hover.border`) }} _focus={{ borderColor: $g1Color(`form.field.select.border`) }}>
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                }
                            </HStack>
                        </FormControl> */}

                        <FormControl>
                            <G1Input component="form" name="apid" type="hidden" />
                        </FormControl>

                        <Flex direction="row" justifyContent="center" marginTop={3}>
                            { actionButtons.length === 0 ? '' :
                                actionButtons.map((button, index) => (
                                    <G1Button key={index} type="submit" component="form" variant={button.variant} title={button.title} marginRight="2" fontSize="md" data-index={index} />
                                ))
                            }
                            </Flex>
                    </form>
                </SeaShellBox>
            </Box>
        </>
    ) ;
} ;